import { FieldCondition, PayPolicyTemplate, PluginField, PluginSchema, TemplateContent, TemplatePlugin, TemplateStatus } from './types';

export type Scope = 'global' | 'companies';

export interface CompanyChip {
  uuid: string;
  name: string | null;
}

/**
 * What the editor holds. `plugins` are the template's own plugins; Global Calculations loads what
 * they depend on by itself. `params` keeps only overrides, keyed by plugin, for any plugin of the plan.
 */
export interface TemplateDraft {
  name: string;
  description: string;
  status: TemplateStatus;
  scope: Scope;
  companies: CompanyChip[];
  plugins: TemplatePlugin[];
  params: Record<string, Record<string, unknown>>;
  /**
   * What the saved template already holds. day-pay-policies merges an update into it, so a plugin
   * in it cannot be taken off and an override in it is only undone by sending the default.
   */
  stored: { plugins: string[]; params: Record<string, string[]> };
}

const NOTHING_STORED: TemplateDraft['stored'] = { plugins: [], params: {} };

export type SchemaMap = Record<string, PluginSchema>;

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const emptyDraft = (): TemplateDraft => ({ name: '', description: '', status: TemplateStatus.Draft, scope: 'global', companies: [], plugins: [], params: {}, stored: NOTHING_STORED });

/** day-pay-policies stores params as one single-key object per param: [{ limit: 10 }, { scope: 'punch' }]. */
export const paramsFromStored = (stored: unknown): Record<string, unknown> => {
  if (Array.isArray(stored)) return Object.assign({}, ...stored.filter((entry) => entry && typeof entry === 'object'));
  return stored && typeof stored === 'object' ? { ...(stored as Record<string, unknown>) } : {};
};

export const paramsToStored = (params: Record<string, unknown>): Record<string, unknown>[] => Object.entries(params).map(([name, value]) => ({ [name]: value }));

/** A draft from a stored template; a copy starts as a new draft that keeps everything but the identity. */
export const draftFromTemplate = (template: PayPolicyTemplate, asCopy: boolean): TemplateDraft => {
  const companyUuids = template.companyUuids ?? [];
  const params: TemplateDraft['params'] = {};
  (template.defaultParams ?? []).forEach(({ pluginId, params: stored }) => {
    params[pluginId] = paramsFromStored(stored);
  });
  return {
    name: asCopy ? `Copy of ${template.name}` : template.name,
    description: template.description ?? '',
    status: asCopy ? TemplateStatus.Draft : template.status,
    scope: companyUuids.length > 0 ? 'companies' : 'global',
    companies: companyUuids.map((uuid) => ({ uuid, name: null })),
    plugins: template.plugins.map((plugin) => ({ ...plugin })),
    params,
    // A copy is saved as a new template, so nothing of it is stored yet.
    stored: asCopy ? NOTHING_STORED : { plugins: template.plugins.map((plugin) => plugin.pluginId), params: Object.fromEntries(Object.entries(params).map(([pluginId, values]) => [pluginId, Object.keys(values)])) }
  };
};

/** Why a plugin cannot be taken off the template, or null when it can. */
export const removalBlockedBy = (draft: TemplateDraft, pluginId: string): string | null => (draft.stored.plugins.includes(pluginId) ? 'A saved template keeps its plugins: start a new template from this one to leave it out' : null);

export const humanize = (name: string) => {
  const words = name.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** New plugins are pinned to a caret range of their newest version, as the existing templates are. */
export const caretRange = (version: string) => (/^[\^~<>=]/.test(version) ? version : `^${version}`);

const versionParts = (version: string) => version.split(/[.+-]/, 3).map((part) => Number.parseInt(part, 10) || 0);

/** The highest of a plugin's versions by semver, whatever order they are listed in. */
export const newestVersion = (versions: string[]): string =>
  versions.reduce((best, candidate) => {
    const [a, b] = [versionParts(candidate), versionParts(best)];
    const diff = a.map((part, index) => part - b[index]).find((delta) => delta !== 0) ?? 0;
    return diff > 0 ? candidate : best;
  });

/**
 * What a plugin's version picker offers, newest first, each as the caret range the template stores.
 * A range the template already holds that no version produces (">=1.0.0 <2.0.0") is kept at the top,
 * so the picker can show what is stored.
 */
export const versionChoices = (versions: string[], held?: string): string[] => {
  const sorted = [...versions].sort((left, right) => {
    const [a, b] = [versionParts(right), versionParts(left)];
    return a.map((part, index) => part - b[index]).find((delta) => delta !== 0) ?? 0;
  });
  const ranges = Array.from(new Set(sorted.map(caretRange)));
  return held && !ranges.includes(held) ? [held, ...ranges] : ranges;
};

/** The dependencies Global Calculations loads by itself; an optional one only comes in when rooted or required elsewhere. */
export const requiredDependencies = (schema: PluginSchema | undefined) => (schema?.dependencies ?? []).filter((dependency) => !dependency.optional);

/**
 * The plugins Global Calculations loads for the template's own ones, as far as the schemas fetched
 * so far tell; each fetched schema can name more, so this grows until every one is in.
 */
export const dependencyClosure = (plugins: TemplatePlugin[], schemas: SchemaMap): TemplatePlugin[] => {
  const seen = new Set(plugins.map((plugin) => plugin.pluginId));
  const closure: TemplatePlugin[] = [];
  const queue = [...plugins.map((plugin) => plugin.pluginId)];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    requiredDependencies(schemas[id]).forEach((dependency) => {
      if (seen.has(dependency.plugin)) return;
      seen.add(dependency.plugin);
      closure.push({ pluginId: dependency.plugin, version: dependency.version });
      queue.push(dependency.plugin);
    });
  }
  return closure;
};

/** Every plugin of the plan: the template's own and what they load. */
export const planIds = (draft: TemplateDraft, schemas: SchemaMap) => new Set([...draft.plugins, ...dependencyClosure(draft.plugins, schemas)].map((plugin) => plugin.pluginId));

const hasOwn = (record: Record<string, unknown> | undefined, key: string) => record !== undefined && Object.prototype.hasOwnProperty.call(record, key);

export const isOverridden = (draft: TemplateDraft, pluginId: string, field: string) => hasOwn(draft.params[pluginId], field);

/** The value a param takes: the template's override, else the plugin's declared default. */
export const paramValue = (draft: TemplateDraft, schemas: SchemaMap, pluginId: string, field: string): unknown => {
  if (isOverridden(draft, pluginId, field)) return draft.params[pluginId][field];
  return schemas[pluginId]?.fields?.find((candidate) => candidate.name === field)?.default;
};

const REF_RE = /^\$plugin\.([^.]+)\.(.+)$/;

export const conditionHolds = (condition: FieldCondition, draft: TemplateDraft, schemas: SchemaMap): boolean => {
  const plan = planIds(draft, schemas);
  if (condition.plugin !== undefined) return plan.has(condition.plugin) === (condition.present ?? true);
  const ref = condition.field ? REF_RE.exec(condition.field) : null;
  if (!ref) return true;
  const [, pluginId, field] = ref;
  // A param of a plugin the plan leaves out is never set, so no condition on it holds.
  if (!plan.has(pluginId)) return false;
  const value = paramValue(draft, schemas, pluginId, field);
  if (condition.is !== undefined) return value === condition.is;
  if (condition.isNot !== undefined) return value !== condition.isNot;
  if (condition.in !== undefined) return typeof value === 'string' && condition.in.includes(value);
  if (condition.exceeds !== undefined) return typeof value === 'number' && value > condition.exceeds;
  return true;
};

export const isFieldActive = (field: PluginField, draft: TemplateDraft, schemas: SchemaMap) => !field.validation.activeWhen || conditionHolds(field.validation.activeWhen, draft, schemas);

export const MISSING = 'Required';

/**
 * Global Calculations marks every param required. One whose default is an empty text its pattern
 * accepts is complete when left empty, and one with requiredWhen is required only while that holds.
 */
export const isRequired = (field: PluginField, draft: TemplateDraft, schemas: SchemaMap): boolean => {
  const { validation } = field;
  if (validation.requiredWhen) return conditionHolds(validation.requiredWhen, draft, schemas);
  if (field.default === '' && (!validation.pattern || new RegExp(validation.pattern).test(''))) return false;
  return validation.required;
};

/** What is wrong with a param's value, or null. Only what the schema states; day-pay-policies checks the rest on save. */
export const fieldError = (field: PluginField, value: unknown, required = field.validation.required): string | null => {
  const { validation } = field;
  const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
  if (empty) return required ? MISSING : null;
  if (field.kind === 'number') {
    if (typeof value !== 'number' || Number.isNaN(value)) return 'Must be a number';
    if (validation.min !== undefined && value < validation.min) return `Must be at least ${validation.min}`;
    if (validation.max !== undefined && value > validation.max) return `Must be at most ${validation.max}`;
  }
  if (field.kind === 'string' && typeof value === 'string') {
    if (validation.maxLength !== undefined && value.length > validation.maxLength) return `At most ${validation.maxLength} characters`;
    if (validation.pattern && !new RegExp(validation.pattern).test(value)) return 'Wrong format';
    if (field.options && field.options.length > 0 && !field.options.includes(value)) return `Must be one of ${field.options.join(', ')}`;
  }
  if (field.kind === 'array' && Array.isArray(value)) {
    if (validation.minItems !== undefined && value.length < validation.minItems) return `At least ${validation.minItems} items`;
    if (validation.maxItems !== undefined && value.length > validation.maxItems) return `At most ${validation.maxItems} items`;
  }
  return null;
};

/** The error of a plugin's param as the draft stands. */
export const paramError = (field: PluginField, draft: TemplateDraft, schemas: SchemaMap, pluginId: string) => fieldError(field, paramValue(draft, schemas, pluginId, field.name), isRequired(field, draft, schemas));

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Every error that stops a save, keyed `pluginId.field`, plus `name`, `companies` and `plugins`. */
export const draftErrors = (draft: TemplateDraft, schemas: SchemaMap): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!draft.name.trim()) errors.name = 'Required';
  if (draft.scope === 'companies' && draft.companies.length === 0) errors.companies = 'Add at least one company, or choose Global';
  if (draft.plugins.length === 0) errors.plugins = 'Add at least one plugin';
  planIds(draft, schemas).forEach((pluginId) => {
    (schemas[pluginId]?.fields ?? []).forEach((field) => {
      if (!isFieldActive(field, draft, schemas)) return;
      const error = paramError(field, draft, schemas, pluginId);
      // A required param left empty is the pay policy's to fill, so it does not stop the template.
      if (error && error !== MISSING) errors[`${pluginId}.${field.name}`] = error;
    });
  });
  return errors;
};

/**
 * The body day-pay-policies stores. An override equal to the default, of a hidden param, or of a
 * plugin no longer in the plan is dropped unless the saved template holds it; one the saved
 * template holds and the editor has reset is sent as the default, since an update only merges.
 * Params a plugin's schema does not know are kept.
 */
export const draftToContent = (draft: TemplateDraft, schemas: SchemaMap): TemplateContent => {
  const plan = planIds(draft, schemas);
  const pluginIds = Array.from(new Set([...Object.keys(draft.params), ...Object.keys(draft.stored.params)])).filter((pluginId) => plan.has(pluginId));
  const defaultParams = pluginIds
    .map((pluginId) => {
      const values = draft.params[pluginId] ?? {};
      const stored = draft.stored.params[pluginId] ?? [];
      const params: Record<string, unknown> = {};
      Object.entries(values).forEach(([name, value]) => {
        const field = schemas[pluginId]?.fields?.find((candidate) => candidate.name === name);
        if (field && !stored.includes(name) && (!isFieldActive(field, draft, schemas) || sameValue(value, field.default))) return;
        params[name] = value;
      });
      stored.forEach((name) => {
        const field = schemas[pluginId]?.fields?.find((candidate) => candidate.name === name);
        if (!(name in values) && field) params[name] = field.default;
      });
      return { pluginId, params: paramsToStored(params) };
    })
    .filter(({ params }) => params.length > 0);
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    status: draft.status,
    companyUuids: draft.scope === 'companies' ? draft.companies.map((company) => company.uuid) : [],
    plugins: draft.plugins,
    defaultParams
  };
};

/** Minutes as hours and minutes, for duration fields. */
export const splitMinutes = (minutes: number) => ({ hours: Math.floor(minutes / 60), minutes: minutes % 60 });

/** Minutes after midnight as HH:MM, for time_of_day fields. */
export const minutesToClock = (minutes: number) => {
  const { hours, minutes: rest } = splitMinutes(minutes);
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
};

export const clockToMinutes = (clock: string): number | undefined => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock);
  return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
};
